// ./src/components/features/Pois/PoisFilterBar/PoisFilterBar.tsx
//
// Filter controls for the event Points of Interest list: by status (active /
// disabled) and by category (from the shared "poi" vocabulary). Fully controlled —
// the page owns the value and maps it to a PointOfInterestFilter. Name/address
// search lives in the page header, not here.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, CategorySelect } from "@/components/generic";

export type PoiStatusFilter = "active" | "disabled";

export interface PoisFilterValue {
  status: PoiStatusFilter | null;
  categoryId: string | null;
}

export interface PoisFilterBarProps {
  value: PoisFilterValue;
  onChange: (value: PoisFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_POIS_FILTER: PoisFilterValue = {
  status: null,
  categoryId: null,
};

export const poisFilterActiveCount = (value: PoisFilterValue): number =>
  [value.status, value.categoryId].filter(Boolean).length;

const isFilterActive = (value: PoisFilterValue): boolean => poisFilterActiveCount(value) > 0;

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 260px", minWidth: 240 };
const CATEGORY_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };

const ANY = "any";

const PoisFilterBarComponent: React.FC<PoisFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["events", "common"]);

  const statusOptions = [
    { value: ANY, label: t("events:pois.filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("events:pois.filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("events:pois.filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("events:pois.filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) =>
              onChange({ ...value, status: next === ANY ? null : (next as PoiStatusFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={CATEGORY_STYLE}>
        <CategorySelect
          group="poi"
          label={t("events:pois.filters.category.label", { defaultValue: "Category" })}
          placeholder={t("events:pois.filters.category.placeholder", { defaultValue: "All categories" })}
          value={value.categoryId}
          onChange={(categoryId) => onChange({ ...value, categoryId })}
          disabled={disabled}
          clearable
          size="md"
        />
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_POIS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const PoisFilterBar = React.memo(PoisFilterBarComponent);
